import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styles from "./SiteHeader.module.css";

// Priority+ nav: render every item, then on layout measure each one's
// natural width and only show the prefix that fits inside the
// available row. The remainder are reported via `onOverflow` so the
// header can flip on its hamburger button (and the mobile drawer
// continues to expose every item — overflowed or not).
//
// Keeping the items in DOM as `visibility: hidden` rather than fully
// removing them means the next ResizeObserver tick can re-measure
// without a flash. We re-measure on item-set changes too.
export default function ResponsiveNav({ items = [], onOverflow }) {
  const containerRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(items.length);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return;

    function recompute() {
      // 1px buffer absorbs sub-pixel rounding so boundary widths
      // don't oscillate between visibleCount values.
      const containerWidth = container.clientWidth - 1;
      const children = Array.from(measure.children);
      // Use computed gap from the measure row so accumulated widths
      // include the inter-item spacing.
      const cs = getComputedStyle(measure);
      const gap = parseFloat(cs.columnGap || cs.gap || "0") || 0;

      let used = 0;
      let count = 0;
      for (let i = 0; i < children.length; i++) {
        const w = children[i].getBoundingClientRect().width;
        const next = used + (i === 0 ? 0 : gap) + w;
        if (next > containerWidth) break;
        used = next;
        count++;
      }
      setVisibleCount(count);
    }

    recompute();
    const ro = new ResizeObserver(recompute);
    ro.observe(container);
    window.addEventListener("resize", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [items]);

  // Notify the header so it can render the hamburger when anything
  // is hidden. Effect (not layout-effect) so we don't trigger an
  // extra synchronous render on the parent during measurement.
  useEffect(() => {
    onOverflow?.(visibleCount < items.length);
  }, [visibleCount, items.length, onOverflow]);

  const visible = items.slice(0, visibleCount);

  return (
    <nav className={styles.nav} ref={containerRef}>
      {/* Measurement mirror — all items rendered with absolute
          positioning so they don't affect the visible row's layout
          but still produce real getBoundingClientRect widths. */}
      <div className={styles.navMeasure} aria-hidden ref={measureRef}>
        {items.map((item) => (
          <span key={`m-${item.label}`} className={styles.navItem}>
            {item.label}
          </span>
        ))}
      </div>

      {visible.map((item) => (
        <Link key={item.label} to={item.path} className={styles.navItem}>
          {item.label}
        </Link>
      ))}
    </nav>
  );
}
